/**
 * Admin Error Logs Client API
 *
 * Frontend functions for logging and retrieving error logs
 * from the server.
 */

import { apiGet, apiPost, apiPatch } from "./client.js";

export interface ErrorLogSummary {
  id: string;
  timestamp: string;
  severity: "critical" | "error" | "warning" | "info";
  error_code: string;
  message: string;
  context: Record<string, unknown>;
  stack_trace?: string;
  user?: { id: string; email: string };
  website?: { id: string; domain: string };
  occurrence_count: number;
  last_occurred_at: string;
  resolved: boolean;
  warnings: string[];
}

export interface ErrorLogsListResponse {
  items: ErrorLogSummary[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Log a frontend error to the server.
 * Fire-and-forget: doesn't throw, gracefully handles failures.
 */
export async function logFrontendError(
  error_code: string,
  message: string,
  context?: Record<string, unknown>,
  stack_trace?: string,
): Promise<void> {
  try {
    await apiPost<{ id: string; timestamp: string }>("/admin/error-logs", {
      severity: "error",
      error_code,
      message,
      context: {
        ...context,
        surface: context?.surface || "frontend",
        page: context?.page || window.location.pathname,
        page_url: window.location.href,
        is_blocking: context?.is_blocking ?? false,
      },
      stack_trace,
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (err) {
    // Silently fail - don't break the app if error logging fails
    console.error("Failed to log frontend error:", err);
  }
}

/**
 * Log a critical error to the server.
 */
export async function logCriticalError(
  error_code: string,
  message: string,
  context?: Record<string, unknown>,
  stack_trace?: string,
): Promise<void> {
  try {
    await apiPost<{ id: string; timestamp: string }>("/admin/error-logs", {
      severity: "critical",
      error_code,
      message,
      context: {
        ...context,
        surface: context?.surface || "frontend",
        page: context?.page || window.location.pathname,
        page_url: window.location.href,
        is_blocking: context?.is_blocking ?? true,
      },
      stack_trace,
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.error("Failed to log critical error:", err);
  }
}

/**
 * Log a warning to the server.
 */
export async function logWarning(
  error_code: string,
  message: string,
  context?: Record<string, unknown>,
): Promise<void> {
  try {
    await apiPost<{ id: string; timestamp: string }>("/admin/error-logs", {
      severity: "warning",
      error_code,
      message,
      context: {
        ...context,
        surface: context?.surface || "frontend",
        page: context?.page || window.location.pathname,
        page_url: window.location.href,
        is_blocking: context?.is_blocking ?? false,
      },
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.error("Failed to log warning:", err);
  }
}

export interface DigestGroup {
  fingerprint: string;
  error_code: string;
  message: string;
  severity: string;
  surface: string | null;
  route: string | null;
  occurrence_count: number;
  unique_org_count: number;
  unique_website_count: number;
  unique_user_count: number;
  first_seen: string;
  last_seen: string;
  is_blocking: boolean;
  sample_stack_trace: string | null;
  sample_context: Record<string, unknown>;
  priority_score: number;
}

export interface ErrorDigestResponse {
  period_start: string;
  period_end: string;
  generated_at: string;
  total_groups: number;
  total_occurrences: number;
  groups: DigestGroup[];
  markdown: string;
}

/**
 * List error logs (admin only).
 */
export async function listErrorLogs(params?: {
  page?: number;
  limit?: number;
  severity?: string;
  error_code?: string;
  website_id?: string;
}): Promise<ErrorLogsListResponse> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.severity) qs.set("severity", params.severity);
  if (params?.error_code) qs.set("error_code", params.error_code);
  if (params?.website_id) qs.set("website_id", params.website_id);

  const q = qs.toString();
  return apiGet(`/admin/error-logs${q ? `?${q}` : ""}`);
}

/**
 * Get a specific error log (admin only).
 */
export async function getErrorLog(id: string): Promise<ErrorLogSummary> {
  return apiGet(`/admin/error-logs/${id}`);
}

/**
 * Mark an error log as resolved (admin only).
 */
export async function markErrorResolved(id: string): Promise<ErrorLogSummary> {
  return apiPatch(`/admin/error-logs/${id}/resolve`, {});
}

/**
 * Get the daily error digest (admin only).
 */
export async function getErrorDigest(
  hours: number = 24,
): Promise<ErrorDigestResponse> {
  return apiGet(`/admin/error-logs/digest?hours=${hours}`);
}

/**
 * Format error log as JSON for copying to clipboard.
 */
export function formatErrorAsJSON(error: ErrorLogSummary): string {
  return JSON.stringify(
    {
      error_code: error.error_code,
      severity: error.severity,
      message: error.message,
      occurred_at: error.timestamp,
      user: error.user,
      website: error.website,
      context: error.context,
      occurrence_count: error.occurrence_count,
      last_occurred_at: error.last_occurred_at,
      stack_trace: error.stack_trace,
    },
    null,
    2,
  );
}

/**
 * Format error log as Markdown for GitHub issues.
 */
export function formatErrorAsMarkdown(error: ErrorLogSummary): string {
  const lines = [
    `## Error: ${error.error_code}`,
    "",
    `**Severity:** ${error.severity}`,
    `**First Seen:** ${new Date(error.timestamp).toLocaleString()}`,
    `**Last Seen:** ${new Date(error.last_occurred_at).toLocaleString()}`,
    `**Occurrences:** ${error.occurrence_count}`,
    "",
    "### Message",
    "",
    error.message,
    "",
    "### Context",
    "",
  ];

  if (error.user) {
    lines.push(`- **User:** ${error.user.email} (${error.user.id})`);
  }

  if (error.website) {
    lines.push(`- **Website:** ${error.website.domain} (${error.website.id})`);
  }

  lines.push("");

  if (Object.keys(error.context).length > 0) {
    lines.push("### Context Details", "");
    lines.push("```json");
    lines.push(JSON.stringify(error.context, null, 2));
    lines.push("```");
    lines.push("");
  }

  if (error.stack_trace) {
    lines.push("### Stack Trace", "");
    lines.push("```");
    lines.push(error.stack_trace);
    lines.push("```");
    lines.push("");
  }

  lines.push("---");
  lines.push("*Generated by AccessiMonitor Error Logging System*");

  return lines.join("\n");
}
